import React, { useEffect, useRef, useState } from 'react'
import { Chart as ChartJS, TimeScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js'
import { Line } from 'react-chartjs-2'
import moment, { Moment } from 'moment-timezone'
import 'chartjs-adapter-moment'
import axios from 'axios'
import Select from '@mui/material/Select'
import { ListItemText, MenuItem, OutlinedInput } from '@mui/material'
import { t } from '../../../i18n'
import { yearViewOptions, monthViewOptions, weekViewOptions, dayViewOptions } from './graphHelper'
import { LocalizationContextType } from '../../shared/contexts'
import { getParam, applyParams } from './urlParams'

// Register the necessary components with Chart.js
ChartJS.register(TimeScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const scope = 'command_history'

type TimePeriod = 'day' | 'week' | 'month' | 'year'
const availableTimePeriods = ['day', 'week', 'month', 'year']

type GroupingOption = 'command_step' | 'command' | 'queued_order'
const availableGroupingOptions = [{
  label: t('command_steps', { scope }),
  value: 'command_step' as GroupingOption
}, {
  label: t('commands', { scope }),
  value: 'command' as GroupingOption
}, {
  label: t('queued_orders', { scope }),
  value: 'queued_order' as GroupingOption
}];

class TimeRange {
  startTime: Moment
  endTime: Moment
}

class SelectOption {
  label: string
  value: string
}

const buildTimeRange = (anchor: Moment, period: TimePeriod): TimeRange => ({
  startTime: anchor.clone().startOf(period).startOf('day'),
  endTime: anchor.clone().endOf(period).endOf('day')
})

const validTimePeriod = (value: string | null): TimePeriod =>
  value && availableTimePeriods.includes(value) ? (value as TimePeriod) : 'week'

const validGrouping = (value: string | null): GroupingOption =>
  availableGroupingOptions.some(option => option.value === value) ? (value as GroupingOption) : 'command_step'

interface CommandGraphViewProps {
  localization_context?: LocalizationContextType
}

const CommandGraphView: React.FC<CommandGraphViewProps> = ({ localization_context }) => {
  // tenantTz is the IANA name exposed via Location#localization_context (e.g.
  // 'Asia/Tokyo'). Falls back to UTC when no context is provided (e.g. an
  // admin viewing the page without a selected_location). The chart's data
  // points are plotted by their UTC instant; tenantTz only affects how the
  // X-axis tick labels, tooltip titles, and timeRange wall-clock boundaries
  // are computed.
  const tenantTz = localization_context?.timezone || 'UTC'

  const [commandDurations, setCommandDurations] = useState([])
  const [grouping, setGrouping] = useState<GroupingOption>(() => validGrouping(getParam('grouping')))
  const [detailedFilter, setDetailedFilter] = useState<SelectOption | null>(null)
  const [commandDetailedFilterOptions, setCommandDetailedFilterOptions] = useState<SelectOption[]>([])
  const [commandStepDetailedFilterOptions, setCommandStepDetailedFilterOptions] = useState<SelectOption[]>([])

  // Captured once so the async detailed-filter restore survives the URL being
  // rewritten (with filter cleared) before the filter options finish loading.
  const initialFilterValue = useRef(getParam('filter'))

  const initialTimePeriod = validTimePeriod(getParam('time_period'))
  const [timePeriod, setTimePeriod] = React.useState<TimePeriod>(initialTimePeriod)

  const [timeRange, setTimeRange] = React.useState<TimeRange>(() => {
    const anchorParam = getParam('anchor')
    const anchor = anchorParam ? moment.tz(anchorParam, 'YYYY-MM-DD', true, tenantTz) : null
    return buildTimeRange(anchor && anchor.isValid() ? anchor : moment.tz(tenantTz), initialTimePeriod)
  })

  const fetchDurations = async () => {
    axios.get('/admin/command_step_durations/graph_data', {
      params: {
        time_from: timeRange.startTime.toISOString(),
        time_until: timeRange.endTime.toISOString(),
        time_period: timePeriod,
        granularity: grouping,
        search: detailedFilter?.value,
        format: 'json'
      }}).then(({ data }) => {
      setCommandDurations(data)
    })
  }

  const fetchDetailedFilterOptions = () => {
    axios.get('/admin/command_step_durations/available_commands_and_steps', {
    }).then(({ data }) => {
      const commands = data.map((command) => {
        return {
          label: command.command_name,
          value: command.command_id
        }
      })

      const commandSteps = data.map((command) => {
        return command.command_steps.map((step) => {
          return {
            label: step.step_name,
            value: step.step_id
          }
        }).flat()
      })

      const commandStepOptions = commandSteps.flat()
      setCommandDetailedFilterOptions(commands)
      setCommandStepDetailedFilterOptions(commandStepOptions)
      restoreDetailedFilter(commands, commandStepOptions)
    })
  }

  const restoreDetailedFilter = (commands: SelectOption[], commandSteps: SelectOption[]) => {
    const filterValue = initialFilterValue.current
    if (!filterValue) return

    const options = grouping === 'command' ? commands : grouping === 'command_step' ? commandSteps : []
    const match = options.find((option) => String(option.value) === filterValue)
    if (match) setDetailedFilter(match)
  }

  useEffect(() => {
    fetchDetailedFilterOptions()
  }, [])

  useEffect(() => {
    applyParams({
      grouping: grouping === 'command_step' ? null : grouping,
      time_period: timePeriod === 'week' ? null : timePeriod,
      anchor: timeRange.startTime.format('YYYY-MM-DD'),
      filter: detailedFilter ? String(detailedFilter.value) : null
    }, 'replace')
    fetchDurations()
  }, [timeRange, grouping, detailedFilter, timePeriod])

  const handleTimePeriodChange = (timePeriod) => {
    setTimePeriod(timePeriod)
    setTimeRange(buildTimeRange(moment.tz(tenantTz), timePeriod))
  }

  const previousButtonLabel = () => {
    return t(`previous_${timePeriod}`, { scope })
  }

  const nextButtonLabel = () => {
    return t(`next_${timePeriod}`, { scope })
  }

  const moveTimePeriod = (direction) => {
    const dir = {
      previous: -1,
      next: 1
    }[direction]

    const startTime = timeRange.startTime.clone().add(dir, timePeriod).startOf(timePeriod)
    const endTime = startTime.clone().endOf(timePeriod)

    setTimeRange({ startTime, endTime })
  }

  const onPreviousButtonClick = () => {
    moveTimePeriod('previous')
  }

  const onNextButtonClick = () => {
    moveTimePeriod('next')
  }

  const chartData = {
    labels: commandDurations.map(item => moment(item.date).toDate()),
    datasets: [
      {
        label: 'Estimated duration',
        data: commandDurations.map(item => item.avg_estimated_duration),
        fill: false,
        backgroundColor: '#88c999',
        borderColor: '#88c999',
      },
      {
        label: 'Actual duration',
        data: commandDurations.map(item => item.avg_actual_duration),
        fill: false,
        backgroundColor: '#ff6f61',
        borderColor: '#ff6f61',
      },
    ],
  };


  const getXAxisLabel = () => {
    switch (timePeriod) {
      case 'day':
        return timeRange.startTime.format('dddd')
      case 'week':
        return `${timeRange.startTime.format('MMM D')} - ${timeRange.endTime.format('MMM D')}`
      case 'month':
        return timeRange.startTime.format('MMMM')
      case 'year':
        return timeRange.startTime.format('YYYY')
    }
  }


  const getChartOptions = () => {
    switch (timePeriod) {
      case 'day':
        return dayViewOptions(getXAxisLabel, tenantTz);
      case 'week':
        return weekViewOptions(getXAxisLabel, tenantTz);
      case 'month':
        return monthViewOptions(getXAxisLabel, tenantTz);
      case 'year':
        return yearViewOptions(getXAxisLabel, tenantTz);
    }
  }

  const updateDetailedFilter = (value) => {
    if(grouping == 'command' as GroupingOption) {
      setDetailedFilter(commandDetailedFilterOptions.find((option) => option.value == value))
    } else if(grouping == 'command_step' as GroupingOption) {
      setDetailedFilter(commandStepDetailedFilterOptions.find((option) => option.value == value))
    }
  }

  const onGroupingChange = (grouping) => {
    setGrouping(grouping)
    setDetailedFilter(null)
  }

  return (
    <div className='command-history-graph'>
      <div className='command-filter-bar'>
        <div className='command-filter command-filter-select'>
          <span className='command-filter-label'>{t('time_period', { scope })}</span>
          <Select
            className='react-select'
            value={timePeriod}
            onChange={(opt) => handleTimePeriodChange(opt.target.value)}
            input={<OutlinedInput />}
            MenuProps={{ PaperProps: { style: { maxHeight: 224, width: 250 } } }}
          >
            {availableTimePeriods.map((option) => (
              <MenuItem key={option} value={option}>
                <ListItemText primary={t(option, { scope })} />
              </MenuItem>
            ))}
          </Select>
        </div>

        <div className='command-filter command-filter-select'>
          <span className='command-filter-label'>{t('group_data_by', { scope })}</span>
          <Select
            className='react-select'
            value={grouping}
            onChange={(opt) => onGroupingChange(opt.target.value as GroupingOption)}
            input={<OutlinedInput />}
            MenuProps={{ PaperProps: { style: { maxHeight: 224, width: 250 } } }}
          >
            {availableGroupingOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </Select>
        </div>

        { grouping != 'queued_order' as GroupingOption &&
          <div className='command-filter command-filter-detailed'>
            <span className='command-filter-label'>{t('detailed_filter', { scope })}</span>
            <Select
              labelId="specific-command"
              className='react-select'
              value={detailedFilter?.value || ''}
              displayEmpty
              renderValue={(selected) => {
                if (selected === '') {
                  return <span>
                    {t('select_specific', { scope })} {t(grouping, { scope })} <em>{ t('optional', { scope }) }</em>
                  </span>;
                }
                return detailedFilter?.label;
              }}
              onChange={(opt) => updateDetailedFilter(opt.target.value)}
              input={<OutlinedInput />}
              MenuProps={{ PaperProps: { style: { maxHeight: 224, width: 250 } } }}
            >
              { grouping == 'command' as GroupingOption &&
                  commandDetailedFilterOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  ))
              }
              { grouping == 'command_step' as GroupingOption &&
                  commandStepDetailedFilterOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  ))
              }
              <MenuItem value={null}>
                <ListItemText primary={t('clear', { scope })} />
              </MenuItem>
            </Select>
          </div>
        }
      </div>

      <Line data={chartData} options={getChartOptions()} height={100} />
      <div className="time-period-navigation-container">
        <button
          className='btn btn-secondary'
          onClick={onPreviousButtonClick}
        >
          <i className='fa fa-chevron-left' style={{ marginRight: '5px' }} />
          {previousButtonLabel()}
        </button>
        <button
          className='btn btn-secondary'
          onClick={onNextButtonClick}
        >
          {nextButtonLabel()}
          <i className='fa fa-chevron-right' style={{ marginLeft: '5px' }} />
        </button>
      </div>
    </div>
  )
}

export default CommandGraphView
